import React, { useEffect, useState } from "react";
import { TableRow, TableCell, Box, useTheme } from "@mui/material";
import Moment from "react-moment";
import { useSnackbar } from "notistack";
import { motion, AnimatePresence } from "framer-motion";
import TableView from "./util/TableView";
import StringArrayList from "./util/StringArrayList";
import { api } from "../api/api";
import { ADMIN, SENDER } from "../ListItems";
import { ModuleHeader } from "../../components/composed";
import { Badge, Button } from "../../components/primitives";
import { FadeIn } from "../../components/motion";

export default function HistoryView(props) {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const snackBarAddMessage = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  useEffect(() => {
    props.setShowSearch(false);
  }, [props]);

  const tableHeader = (
    <TableRow>
      <TableCell>Name</TableCell>
      <TableCell>Emails</TableCell>
      <TableCell>Date</TableCell>
      <TableCell>Sender</TableCell>
      <TableCell>Type</TableCell>
      <TableCell>Status</TableCell>
      <TableCell>Actions</TableCell>
    </TableRow>
  );

  const tableRows = (row, index) => {
    let emails = [row.receiver.epamEmail, ...row.receiver.emails];
    return (
      <TableRow key={row.id}>
        <TableCell>{row.receiver.name}</TableCell>
        <TableCell>
          <StringArrayList strings={emails} />
        </TableCell>
        <TableCell>
          <Moment date={row.date} format="HH:mm:SS DD/MM/YYYY" />
        </TableCell>
        <TableCell>{row.sender.name}</TableCell>
        <TableCell>
          <Badge
            variant="neutral"
            size="small"
          >
            {row.type.toLowerCase().replaceAll("_", " ")}
          </Badge>
        </TableCell>
        <TableCell>
          <AnimatePresence mode="wait">
            <motion.div
              key={row.status}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30,
              }}
              style={{ display: 'inline-block' }}
            >
              <Badge
                variant={row.status === "COLLECTED" ? "success" : "neutral"}
                size="small"
              >
                {row.status.toLowerCase().replaceAll("_", " ")}
              </Badge>
            </motion.div>
          </AnimatePresence>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {row.status === "NOTIFICATION_SENT" &&
              row.type !== "REMINDER" &&
              row.type !== "COURIER_MATCH" && (
                <>
                  <Button
                    onClick={() => {
                      callMarkCollected(row.id, index);
                    }}
                    variant="contained"
                    size="small"
                  >
                    Collected
                  </Button>

                  {(props.user.roles.includes(ADMIN) ||
                    props.user.roles.includes(SENDER)) &&
                    row.type === "REGULAR" && (
                      <Button
                        onClick={() => {
                          callSendReminder(row.id);
                        }}
                        variant="outlined"
                        size="small"
                      >
                        Remind
                      </Button>
                    )}
                </>
              )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const callMarkCollected = (id, index) => {
    (async () => {
      api.history
        .markCollected(id)
        .then((log) => {
          let newArr = data.slice(0, data.length);
          newArr[index] = log;
          setData(newArr);
          snackBarAddMessage("Marked as collected", "success");
        })
        .catch((err) => {
          switch (err.status) {
            case 403:
              snackBarAddMessage(
                "You don't have permission to do that!",
                "error"
              );
              break;
            case 404:
              snackBarAddMessage(
                "Record doesn't exists, please refresh page",
                "error"
              );
              break;
            default:
          }
        });
    })();
  };

  const callSendReminder = (id) => {
    (async () => {
      api.sender
        .sendReminder(id)
        .then((_) => {
          snackBarAddMessage("Reminder sent", "success");
        })
        .catch((err) => {
          switch (err.status) {
            case 403:
              snackBarAddMessage(
                "You don't have permission to do that!",
                "error"
              );
              break;
            case 404:
              snackBarAddMessage(
                "Record doesn't exists, please refresh page",
                "error"
              );
              break;
            default:
          }
        });
    })();
  };

  let currentUserUrlAddition = props.userId ? "/currentUser" : "";

  return (
    <FadeIn>
      {!props.userId && (
        <ModuleHeader
          title="History"
          subtitle="Track sent notifications and their status"
          moduleColor={theme.palette.primary.main}
        />
      )}
      <TableView
        fetchUrl={"history/sent-letters" + currentUserUrlAddition}
        tableHeader={tableHeader}
        tableRows={tableRows}
        data={data}
        setData={setData}
        {...props}
      />
    </FadeIn>
  );
}
